import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { supabase } from './supabaseClient';
import AuthPage from './AuthPage';
import AuthConfirmPage from './AuthConfirmPage';
import ResetPasswordPage from './ResetPasswordPage';
import TermsPage from './TermsPage';
import DeleteAccountPage from './DeleteAccountPage';
import DeploymentsPage from './DeploymentsPage';
import ResourcesPage from './ResourcesPage';
import ResourcesTimeline from './ResourcesTimeline';
import ArtistIndex from './ArtistIndex';
import ArtistProfile from './ArtistProfile';
import SetSummary from './SetSummary';
import FeedbackWidget from './FeedbackWidget';
import Checklist from './Checklist';
import ProfileSetup from './ProfileSetup';
import PublicProfile from './PublicProfile';
import ProfileEdit from './ProfileEdit';
import UserNoticeModal from './components/UserNoticeModal';
import SetBrowser from './SetBrowser';
import PublicGallery from './PublicGallery';
import ScanTest from './ScanTest';
import ScanId from './ScanId';
import AiCardScanner from './AiCardScanner';
import AdminChecklists from './AdminChecklists';
import AdminSetSummariesList from './AdminSetSummariesList';
import AdminHome from './AdminHome';
import AdminSetSummaryEdit from './AdminSetSummaryEdit';
import AdminChecklistsTopps from './AdminChecklistsTopps';
import AdminProductRoadmap from './AdminProductRoadmap';
import AdminSetIngestion from './AdminSetIngestion';
const AdminDashboard = lazy(() => import('./AdminDashboard'));
import EditSet from './EditSet';
import EditSubSet from './EditSubSet';
import { SET_REGISTRY } from './setRegistry';
import CommunityChecklist from './CommunityChecklist';
import CommunitySetView from './CommunitySetView';
import Marketplace from './Marketplace';
import ProfilePage from './ProfilePage';
import Messages from './Messages';
import Conversation from './Conversation';
import { trackSignIn, trackSessionStart, trackSessionEnd } from './activityTracker';

export default function App() {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [showEditProfile, setShowEditProfile] = useState(false);
  // A notice this collector must acknowledge before the app renders.
  const [pendingNotice, setPendingNotice] = useState(null);

  // Pending notice lookup. Runs on sign-in and stays quiet on failure: a notice
  // that cannot be fetched must never lock a collector out of their collection.
  useEffect(() => {
    const uid = session?.user?.id;
    if (!uid) { setPendingNotice(null); return; }
    let cancelled = false;
    supabase
      .from('user_notices')
      .select('id, notice_key, title, body, button_label')
      .eq('user_id', uid)
      .is('acknowledged_at', null)
      .order('created_at', { ascending: true })
      .limit(1)
      .maybeSingle()
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) { console.warn('[App] notice lookup failed (non-fatal):', error.message); return; }
        setPendingNotice(data || null);
      });
    return () => { cancelled = true; };
  }, [session?.user?.id]);
  const [showResetPassword, setShowResetPassword] = useState(false);
  // Whether a session already existed when the auth event fired. Supabase emits
  // SIGNED_IN on every page load / session restore, not just real logins — only
  // the signed-out → signed-in transition counts as a sign-in.
  const hadSessionRef = useRef(false);
  let [currentPath, setCurrentPath] = useState(window.location.pathname);

  useEffect(() => {
    const handleNav = () => setCurrentPath(window.location.pathname);
    window.addEventListener('popstate', handleNav);
    return () => window.removeEventListener('popstate', handleNav);
  }, []);

  useEffect(() => {
    const BRAND = 'CollectionKeeper';
    let path = currentPath;
    if (path.length > 1 && path.endsWith('/')) path = path.slice(0, -1);

    // Set-summary pages own their title (SetSummary.js sets it after fetch).
    if (/^\/resources\/sets\/[a-z0-9-]+$/.test(path)) return;
    // Artist profile pages own their title (ArtistProfile.js sets it after fetch).
    if (/^\/resources\/artists\/[a-z0-9-]+$/.test(path)) return;
    // Artist approval pages own their title too.
    if (/^\/artists\/approve\//.test(path)) return;

    let title = BRAND;
    let m;
    if (path === '/' || path === '') title = `${BRAND} · Topps Star Wars Checklists`;
    else if (path === '/messages' || /^\/messages\/[a-zA-Z0-9_-]+$/.test(path)) title = `Messages · ${BRAND}`;
    else if (path === '/marketplace') title = `Marketplace · ${BRAND}`;
    else if (path === '/profile') title = `My Profile · ${BRAND}`;
    else if (path === '/community') title = `Community Checklists · ${BRAND}`;
    else if (/^\/community\/[a-zA-Z0-9_-]+$/.test(path)) title = `Community · ${BRAND}`;
    else if ((m = path.match(/^\/u\/([a-zA-Z0-9_-]+)$/))) title = `@${m[1]} · ${BRAND}`;
    else if (/^\/gallery\//.test(path)) title = `Gallery · ${BRAND}`;
    else if (path === '/resources') title = `Resources · ${BRAND}`;
    else if (path === '/resources/timeline') title = `Timeline · ${BRAND}`;
    else if (path === '/resources/artists') title = `Sketch Artists · ${BRAND}`;
    else if (path === '/auth/confirm') title = `Confirm Email · ${BRAND}`;
    else if (path === '/terms') title = `Terms · ${BRAND}`;
    else if (path === '/delete-account') title = `Delete Your Account · ${BRAND}`;
    else if (path === '/deployments') title = `Deployments · ${BRAND}`;
    else if (path.startsWith('/admin')) title = `Admin · ${BRAND}`;
    else if (path === '/scan-test' || path === '/scan-id') title = `Scan · ${BRAND}`;
    else if (path === '/scan-ai-card') title = `AI Card Scanner · ${BRAND}`;
    else if ((m = path.match(/^\/sets\/([a-zA-Z0-9_-]+)$/))) {
      const meta = SET_REGISTRY.find(s => s.id === m[1]);
      title = meta ? `${meta.name} · ${BRAND}` : BRAND;
    }
    document.title = title;
  }, [currentPath]);

  const loadProfile = async (userId) => {
    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();
    setProfile(data || null);
    setLoadingAuth(false);
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      hadSessionRef.current = !!session;
      setSession(session);
      if (session) {
        trackSessionStart(session.user.id);
        loadProfile(session.user.id);
      } else setLoadingAuth(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setSession(session);
        setShowResetPassword(true);
        setLoadingAuth(false);
        return;
      }
      if (event === 'SIGNED_IN') {
        if (!hadSessionRef.current) trackSignIn(session?.user?.id);
        hadSessionRef.current = true;
        trackSessionStart(session?.user?.id);
      }
      if (event === 'SIGNED_OUT') {
        hadSessionRef.current = false;
        trackSessionEnd();
      }
      setSession(session);
      if (session) loadProfile(session.user.id);
      else { setProfile(null); setLoadingAuth(false); }
    });

    return () => subscription.unsubscribe();
  }, []);

  if (currentPath.length > 1 && currentPath.endsWith('/')) currentPath = currentPath.slice(0, -1);
  if (currentPath === '/auth/confirm') return <AuthConfirmPage />;
  if (currentPath === '/terms') return <TermsPage />;
  // Public, signed-out-reachable: the URL Play's Data Safety form requires and
  // the one we give Apple for 5.1.1(v). Must stay above every auth gate.
  if (currentPath === '/delete-account') return <DeleteAccountPage />;
  if (currentPath === '/deployments') return <DeploymentsPage />;
  if (currentPath === '/resources') return <ResourcesPage />;
  if (currentPath === '/resources/timeline') return <ResourcesTimeline />;
  if (currentPath === '/resources/artists') return <ArtistIndex />;
  const artistProfileMatch = currentPath.match(/^\/resources\/artists\/([a-z0-9-]+)$/);
  if (artistProfileMatch) return <ArtistProfile artistSlug={artistProfileMatch[1]} />;
  const artistApprovalMatch = currentPath.match(/^\/artists\/approve\/([0-9a-fA-F-]{36})$/);
  if (artistApprovalMatch) return <ArtistProfile approvalToken={artistApprovalMatch[1]} />;
  const setSummaryMatch = currentPath.match(/^\/resources\/sets\/([a-z0-9-]+)$/);
  if (setSummaryMatch) return <SetSummary setId={setSummaryMatch[1]} />;
  // marketplace moved to after auth check

  const profileMatch = currentPath.match(/^\/u\/([a-zA-Z0-9_-]+)$/);
  const publicUsername = profileMatch ? profileMatch[1] : null;
  if (publicUsername) return <PublicProfile username={publicUsername} />;

  // Check if we're on a public gallery URL: /gallery/:username/:setId
  const galleryMatch = currentPath.match(/^\/gallery\/([a-zA-Z0-9_-]+)\/([a-zA-Z0-9_-]+)$/);
  if (galleryMatch) return <PublicGallery username={galleryMatch[1]} setId={galleryMatch[2]} />;

  const setMatch = currentPath.match(/^\/sets\/([a-zA-Z0-9_-]+)$/);
  const setId = setMatch ? setMatch[1] : null;

  if (loadingAuth) return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ fontFamily: "'Orbitron', monospace", fontSize: '12px', letterSpacing: '4px', color: 'var(--muted-2)' }}>INITIALIZING...</div>
    </div>
  );

  if (showResetPassword) return <ResetPasswordPage onDone={() => setShowResetPassword(false)} />;
  if (!session) return <AuthPage />;

  if (!profile) return (
    <ProfileSetup
      user={session.user}
      onComplete={() => loadProfile(session.user.id)}
    />
  );

  // Gate the whole app on an unacknowledged notice. Placed after the profile
  // check so a brand-new collector finishes setup first, and before every route
  // below so there is no way round it by navigating.
  if (pendingNotice) return (
    <UserNoticeModal
      notice={pendingNotice}
      onAcknowledged={() => setPendingNotice(null)}
    />
  );

  if (showEditProfile) return (
    <ProfileEdit
      user={session.user}
      profile={profile}
      onComplete={() => { loadProfile(session.user.id); setShowEditProfile(false); }}
      onCancel={() => setShowEditProfile(false)}
    />
  );

  if (currentPath === '/scan-test') {
    const host = window.location.hostname;
    const isStaging = host.includes('netlify.app') || host === 'localhost' || host.startsWith('127.');
    if (!isStaging) {
      return <div style={{ padding: '40px', fontFamily: 'monospace', color: 'var(--text)', background: 'var(--bg)', minHeight: '100vh' }}>Not available in production.</div>;
    }
    return <ScanTest />;
  }
  if (currentPath === '/scan-id') {
    const host = window.location.hostname;
    const isStaging = host.includes('netlify.app') || host === 'localhost' || host.startsWith('127.');
    if (!isStaging) {
      return <div style={{ padding: '40px', fontFamily: 'monospace', color: 'var(--text)', background: 'var(--bg)', minHeight: '100vh' }}>Not available in production.</div>;
    }
    return <ScanId />;
  }
  if (currentPath === '/scan-ai-card') {
    // Availability gate lives inside the component (src/scannerAccess.js).
    return <AiCardScanner user={session.user} />;
  }
  const adminEditSubSetMatch = currentPath.match(/^\/admin\/checklists\/edit\/([a-z0-9-]+)\/subset\/([a-z0-9-]+)$/);
  if (adminEditSubSetMatch) {
    // Admin access controlled by Supabase RLS (admin_users table).
    return <EditSubSet user={session.user} setSlug={adminEditSubSetMatch[1]} subSetSlug={adminEditSubSetMatch[2]} />;
  }
  const adminEditMatch = currentPath.match(/^\/admin\/checklists\/edit\/([a-z0-9-]+)$/);
  if (adminEditMatch) {
    // Admin access controlled by Supabase RLS (admin_users table).
    return <EditSet user={session.user} slug={adminEditMatch[1]} />;
  }
  if (currentPath === '/admin') {
    return <AdminHome user={session.user} />;
  }

  if (currentPath === '/admin/product-roadmap') {
    return <AdminProductRoadmap user={session.user} />;
  }
  if (currentPath === '/admin/dashboard') {
    return (
      <Suspense fallback={<div style={{ minHeight: '100vh', background: 'var(--bg)', color: 'var(--cyan)', fontFamily: "'Orbitron', monospace", fontSize: 11, letterSpacing: 4, textAlign: 'center', paddingTop: 120 }}>// LOADING DASHBOARD</div>}>
        <AdminDashboard user={session.user} />
      </Suspense>
    );
  }
  if (currentPath === '/admin/set-ingestion') {
    return <AdminSetIngestion user={session.user} />;
  }
  if (currentPath === '/admin/checklists/topps') {
    // Admin access controlled by Supabase RLS (admin_users table).
    return <AdminChecklistsTopps user={session.user} />;
  }
  if (currentPath === '/admin/checklists') {
    // Admin access controlled by Supabase RLS (admin_users table).
    return <AdminChecklists user={session.user} />;
  }
  if (currentPath === '/admin/set-summaries') {
    // Admin access controlled by is_admin flag on profiles (checked inside component).
    return <AdminSetSummariesList user={session.user} />;
  }
  const adminSummaryEditMatch = currentPath.match(/^\/admin\/set-summaries\/([a-z0-9-]+)$/);
  if (adminSummaryEditMatch) {
    return <AdminSetSummaryEdit user={session.user} setId={adminSummaryEditMatch[1]} />;
  }
    if (currentPath === '/messages') return <Messages user={session.user} />;
  const conversationMatch = currentPath.match(/^\/messages\/([a-zA-Z0-9_-]+)$/);
  if (conversationMatch) return <Conversation user={session.user} conversationId={conversationMatch[1]} />;
  if (currentPath === '/profile') return <ProfilePage user={session.user} onBack={() => { window.history.pushState({}, '', '/'); window.dispatchEvent(new PopStateEvent('popstate')); }} />;
  if (currentPath === '/marketplace') return <><Marketplace user={session.user} /></>;
  if (currentPath === '/community') return <><CommunityChecklist /><FeedbackWidget user={session?.user} /></>;
  const communitySetMatch = currentPath.match(/^\/community\/([a-zA-Z0-9_-]+)$/);
  if (communitySetMatch) return <><CommunitySetView setId={communitySetMatch[1]} /><FeedbackWidget user={session?.user} /></>;

  if (setId) {
    const setMeta = SET_REGISTRY.find(s => s.id === setId);
    if (setMeta) {
      return (
        <>
        <Checklist
          key={setId}
          setId={setId}
          user={session.user}
          profile={profile}
          onEditProfile={() => setShowEditProfile(true)}
          onBack={() => { window.history.pushState({}, '', '/'); window.dispatchEvent(new PopStateEvent('popstate')); }}
        />
        <FeedbackWidget user={session?.user} />
        </>
      );
    }
  }

  return (
    <>
    <SetBrowser
      user={session.user}
      profile={profile}
      onSignOut={() => supabase.auth.signOut()}
      onEditProfile={() => setShowEditProfile(true)}
    />
    <FeedbackWidget user={session?.user} />
    </>
  );
}
